import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Chip,
  AppBar,
  Toolbar,
  IconButton,
  useTheme,
  useMediaQuery,
  Paper,
  Avatar,
  Divider,
  Dialog,
  DialogContent
} from '@mui/material';
import {
  Code as CodeIcon,
  School as SchoolIcon,
  Work as WorkIcon,
  Email as EmailIcon,
  LinkedIn as LinkedInIcon,
  GitHub as GitHubIcon,
  Login as LoginIcon,
  Star as StarIcon,
  Cloud as CloudIcon,
  Message as MessageIcon,
  Analytics as AnalyticsIcon,
  Quiz as QuizIcon,
  Launch as LaunchIcon,
  MusicNote as MusicNoteIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { getFeebowMediaKit, getImmigrationCanadaMediaKit } from './landing.api';
import LanguageSelector from '../../components/LanguageSelector/LanguageSelector';
import QuoteModal from '../../components/QuoteModal/QuoteModal';
import { ContactEnum } from '../../utils/ContactEnum';
import imageProfile from '../../assets/perfil.png';
import icn1 from '../../assets/thumbnails-screenshots/immigration-canada/1-home.png';
import icn2 from '../../assets/thumbnails-screenshots/immigration-canada/2-features.png';
import icn3 from '../../assets/thumbnails-screenshots/immigration-canada/3-notificações.png';
import feebow1 from '../../assets/thumbnails-screenshots/feebow/1-candidates-list.png';
import feebow2 from '../../assets/thumbnails-screenshots/feebow/2-extraction-resume.png';
import feebow3 from '../../assets/thumbnails-screenshots/feebow/3-list-opportunities.png';
import feebow4 from '../../assets/thumbnails-screenshots/feebow/4-list-opportunities-details.png';
import feebow5 from '../../assets/thumbnails-screenshots/feebow/5-list-opportunities-details-enroll.png';
import feebow6 from '../../assets/thumbnails-screenshots/feebow/6-list-customers.png';
import feebow7 from '../../assets/thumbnails-screenshots/feebow/7-enroll-opportunities.png';

type ProjectItem = {
  title: string;
  description: string;
  technologies: string[];
  statusKey: 'available' | 'in_development';
  icon: React.ReactNode;
  link?: string;
  hasLink: boolean;
  youtubeId?: string;
  youtubeUrl?: string;
  showStatus?: boolean;
};

const Landing: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { isAuthenticated } = useAuth();
  const [quoteModalOpen, setQuoteModalOpen] = useState(false);
  const [quoteModalType, setQuoteModalType] = useState<ContactEnum>(ContactEnum.REQUEST_BUDGET);
  const [mediaKitLoading, setMediaKitLoading] = useState(false);
  const [icnMediaKitLoading, setIcnMediaKitLoading] = useState(false);
  const [icnLightboxOpen, setIcnLightboxOpen] = useState(false);
  const [icnCarouselIndex, setIcnCarouselIndex] = useState(0);
  const icnScreens = [icn1, icn2, icn3];
  const [feebowLightboxOpen, setFeebowLightboxOpen] = useState(false);
  const [feebowCarouselIndex, setFeebowCarouselIndex] = useState(0);
  const feebowScreens = [feebow1, feebow2, feebow3, feebow4, feebow5, feebow6, feebow7];

  useEffect(() => {
    const isPt = i18n.language?.toLowerCase().startsWith('pt');
    document.documentElement.lang = isPt ? 'pt-BR' : 'en';

    const title = isPt ? 'WG Tech IT | Produtos e Soluções' : 'WG Tech IT | Products and Solutions';
    const description = isPt
      ? 'Soluções de software e produtos digitais. Conheça o Imigra Pulse Canadá e outras iniciativas.'
      : 'Software solutions and digital products. Explore Imigra Pulse Canada and other initiatives.';
    const canonical = `${window.location.origin}/`;
    const head = document.head;

    const upsertMeta = (selector: string, attrs: Record<string, string>, content: string) => {
      let el = head.querySelector(selector) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        for (const [k, v] of Object.entries(attrs)) {
          el.setAttribute(k, v);
        }
        head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    const upsertLink = (selector: string, attrs: Record<string, string>, href: string) => {
      let el = head.querySelector(selector) as HTMLLinkElement | null;
      if (!el) {
        el = document.createElement('link');
        for (const [k, v] of Object.entries(attrs)) {
          el.setAttribute(k, v);
        }
        head.appendChild(el);
      }
      el.setAttribute('href', href);
    };

    document.title = title;
    upsertMeta('meta[name="description"]', { name: 'description' }, description);
    upsertMeta('meta[name="robots"]', { name: 'robots' }, 'index,follow');
    upsertMeta('meta[name="googlebot"]', { name: 'googlebot' }, 'index,follow');
    upsertLink('link[rel="canonical"]', { rel: 'canonical' }, canonical);
    upsertMeta('meta[property="og:title"]', { property: 'og:title' }, title);
    upsertMeta('meta[property="og:description"]', { property: 'og:description' }, description);
    upsertMeta('meta[property="og:url"]', { property: 'og:url' }, canonical);
    upsertMeta('meta[property="og:type"]', { property: 'og:type' }, 'website');
  }, [i18n.language]);

  // Media Kit API route (fixed)

  const handleLoginClick = () => {
    if (isAuthenticated) {
      navigate('/imigra-pulse-canada/painel');
    } else {
      navigate('/login');
    }
  };

  const handleLearnMoreImigraPulse = () => {
    navigate('/imigra-pulse-canada');
    setTimeout(() => window.scrollTo(0, 0), 0);
  };

  const handleOpenQuoteModal = (type: ContactEnum) => {
    setQuoteModalType(type);
    setQuoteModalOpen(true);
  };

  const handleCloseQuoteModal = () => {
    setQuoteModalOpen(false);
  };

  const handleQuoteSubmit = (
    data: { name: string; email: string; companyName: string; description: string },
    type: ContactEnum
  ) => {
    // TODO: Connect to API endpoint
    console.log('Quote submission:', { type, data });
  };

  // Fetch Media Kit URL and open in a new tab (using shared httpClient)
  const handleOpenFeebowMediaKit = async () => {
    try {
      setMediaKitLoading(true);
      const lng = i18n.language;
      const payload = await getFeebowMediaKit(lng);
      const url = payload?.data as string | undefined;
      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer');
      } else {
        console.error('URL do mídia kit não encontrada na resposta da API.');
        alert(t('landing_media_kit_error_no_url'));
      }
    } catch (err) {
      console.error('Falha ao carregar o mídia kit do Feebow:', err);
      alert(t('landing_media_kit_error_fetch'));
    } finally {
      setMediaKitLoading(false);
    }
  };

  // Fetch ICN Media Kit URL and open in a new tab (using shared httpClient)
  const handleOpenIcnMediaKit = async () => {
    try {
      setIcnMediaKitLoading(true);
      const lng = i18n.language;
      const payload = await getImmigrationCanadaMediaKit(lng);
      const url = payload?.data as string | undefined;
      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer');
      } else {
        console.error('URL do mídia kit (ICN) não encontrada na resposta da API.');
        alert(t('landing_media_kit_error_no_url'));
      }
    } catch (err) {
      console.error('Falha ao carregar o mídia kit do ICN:', err);
      alert(t('landing_media_kit_error_fetch'));
    } finally {
      setIcnMediaKitLoading(false);
    }
  };

  const skills = [
    '.NET', 'Azure', 'SQL Server', 'MongoDB', 'React', 'TypeScript', 'CI/CD',
    'Microservices', "Serverless", "Message brokers", 'REST APIs', "CQRS", "DDD",
    "AI Development Copilots", "C4 Model", "Git", "ETL", "Python", "jQuery", "Neo4j"
  ];

  const projects: ProjectItem[] = [
    {
      title: 'ImigraPulse Canada',
      description: t('project_icn_description'),
      technologies: ['.NET', 'React', 'Azure', 'TypeScript', 'Serverless', 'Telegram API', 'Image API' ],
      statusKey: 'available' as const,
      icon: <MessageIcon />,
      link: '/imigra-pulse-canada',
      hasLink: true
    },
    /*{
      title: 'Mentoria para Concurseiros',
      description: 'Plataforma completa para preparação de concursos públicos com simulados inteligentes, dashboards de progresso e tutoria com IA para maximizar suas chances de aprovação.',
      technologies: ['React', 'TypeScript', 'IA/RAG', 'Analytics', 'Gamification', 'AWS'],
      status: 'Em desenvolvimento',
      icon: <QuizIcon />,
      link: '/exam-prep',
      hasLink: true
    },*/
    {
      title: 'Feebow HR Tech',
      description: t('project_feebow_description'),
      technologies: ['.NET', 'React', 'Azure', 'Typescript', 'WhatsApp API', 'Telegram API', 'Message Brokers'],
      statusKey: 'in_development' as const,// 'standby',
      icon: <WorkIcon />,
      link: '/hrtech',
      hasLink: false
    },
    {
      title: 'Band Ahead (2018)',
      description: t('project_band_ahead_description'),
      technologies: ['.NET', 'jQuery', 'JavaScript', 'SQL Server', 'Neo4j', 'Google Maps API'],
      statusKey: 'available' as const,
      icon: <MusicNoteIcon />,
      link: undefined,
      hasLink: false,
      showStatus: false,
      youtubeId: 'QWgADq-uIuw',
      youtubeUrl: 'https://www.youtube.com/watch?v=QWgADq-uIuw'
    }
  ];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Header */}
      <AppBar position="static" elevation={0} sx={{ bgcolor: 'primary.main' }}>
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1, fontWeight: 'bold' }}>
            {t('landing_page_title_main')}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <LanguageSelector selectedColor="#fff" />
            <Button
              color="inherit"
              startIcon={<LoginIcon />}
              onClick={handleLoginClick}
              sx={{
                bgcolor: 'rgba(255,255,255,0.1)',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' }
              }}
            >
              {isAuthenticated ? t('routes_login_access') : t('routes_login_label')}
            </Button>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Hero Section */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          color: 'white',
          py: { xs: 8, md: 12 },
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid component={'div' as React.ElementType} item xs={12} md={8}>
              <Typography
                variant={isMobile ? 'h3' : 'h2'}
                component="h1"
                gutterBottom
                sx={{ fontWeight: 'bold', mb: 3 }}
              >
                {t('landing_page_title_hero')}
              </Typography>
              <Typography variant="h6" sx={{ mb: 4, opacity: 0.9, lineHeight: 1.6 }}>
                {t('landing_page_description_hero')}
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  size="large"
                  sx={{
                    bgcolor: 'white',
                    color: 'primary.main',
                    '&:hover': { bgcolor: 'grey.100' }
                  }}
                  startIcon={<EmailIcon />}
                  onClick={() => handleOpenQuoteModal(ContactEnum.SUPPORT)}
                >
                  {t('landing_page_btn_contact')}
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  sx={{
                    borderColor: 'white',
                    color: 'white',
                    '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' }
                  }}
                  onClick={handleLearnMoreImigraPulse}
                >
                  {t('landing_projects_learn_more')}
                </Button>
              </Box>
            </Grid>
            <Grid component={'div' as React.ElementType} item xs={12} md={4}>
              <Box sx={{ textAlign: 'center' }}>
                <Avatar
                  src={imageProfile}
                  sx={{
                    width: { xs: 150, md: 200 },
                    height: { xs: 150, md: 200 },
                    bgcolor: 'secondary.main',
                    fontSize: { xs: '3rem', md: '4rem' },
                    mx: 'auto',
                    mb: 2
                  }}
                >
                  WG
                </Avatar>
                <Typography variant="h6" sx={{ opacity: 0.9 }}>
                  William Goi
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.7 }}>
                  Software Architect<br /> & Software Engineer
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Skills Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography variant="h4" component="h2" textAlign="center" gutterBottom sx={{ mb: 6 }}>
          {t('landing_page_title_features')}
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'center' }}>
          {skills.map((skill, index) => (
            <Chip
              key={index}
              label={skill}
              variant="outlined"
              size="medium"
              sx={{
                fontSize: '1rem',
                py: 2,
                px: 1,
                '&:hover': {
                  bgcolor: 'primary.main',
                  color: 'white',
                  transform: 'translateY(-2px)',
                  transition: 'all 0.3s ease'
                }
              }}
            />
          ))}
        </Box>
      </Container>

      {/* Projects Section */}
      <Box sx={{ bgcolor: 'grey.50', py: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="h4" component="h2" textAlign="center" gutterBottom sx={{ mb: 6 }}>
            {t('landing_projects_title')}
          </Typography>
          <Grid container spacing={4}>
            {projects.map((project, index) => (
              <Grid component={'div' as React.ElementType} item xs={12} sm={6} md={4} key={index}>
                <Card
                  sx={{
                    height: '100%',
                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: theme.shadows[8]
                    }
                  }}
                >
                  <CardContent sx={{ p: 4 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Box sx={{ color: 'primary.main', mr: 2 }}>
                        {project.icon}
                      </Box>
                      <Typography variant="h5" component="h3" sx={{ fontWeight: 'bold' }}>
                        {project.title}
                      </Typography>
                    </Box>
                    <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.6, whiteSpace: 'pre-line' }}>
                      {project.description}
                    </Typography>
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                        {t('landing_projects_technologies')}
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {project.technologies.map((tech, techIndex) => (
                          <Chip
                            key={techIndex}
                            label={tech}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: '0.75rem' }}
                          />
                        ))}
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      {project.showStatus !== false && (
                        <Chip
                          label={project.statusKey === 'in_development' ? t('project_status_in_development') : t('project_status_available')}
                          color={project.statusKey === 'in_development' ? 'warning' : 'success'}
                          variant="filled"
                          size="small"
                        />
                      )}
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        {project.hasLink ? (
                          <Button
                            variant="contained"
                            size="small"
                            startIcon={<LaunchIcon />}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              if (project.title === 'ImigraPulse Canada') {
                                navigate('/imigra-pulse-canada');
                              } else {
                                navigate(project.link!);
                              }
                            }}
                            sx={{
                              bgcolor: 'primary.main',
                              '&:hover': { bgcolor: 'primary.dark' },
                              transition: 'background-color 0.2s ease',
                              '&:active': {
                                transform: 'none',
                                boxShadow: 'none'
                              },
                              '&:focus': {
                                outline: 'none'
                              }
                            }}
                          >
                            {project.title === 'ImigraPulse Canada'
                              ? t('landing_projects_learn_more')
                              : t('landing_projects_access')}
                          </Button>
                        ) : (
                          project.title !== 'Feebow HR Tech' && project.title !== 'Band Ahead (2018)' ? (
                            <Button
                              variant="outlined"
                              size="small"
                              disabled
                              sx={{ opacity: 0.6 }}
                            >
                              {t('landing_projects_coming_soon')}
                            </Button>
                          ) : null
                        )}

                        {project.title === 'Feebow HR Tech' && (
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<LaunchIcon />}
                            onClick={handleOpenFeebowMediaKit}
                            sx={{
                              borderColor: 'primary.main',
                              color: 'primary.main',
                              '&:hover': { borderColor: 'primary.dark', color: 'primary.dark' }
                            }}
                            disabled={mediaKitLoading}
                          >
                            {mediaKitLoading ? t('landing_media_kit_opening') : t('landing_media_kit_label')}
                          </Button>
                        )}

                        {project.title === 'ImigraPulse Canada' && (
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<LaunchIcon />}
                            onClick={handleOpenIcnMediaKit}
                            sx={{
                              borderColor: 'primary.main',
                              color: 'primary.main',
                              '&:hover': { borderColor: 'primary.dark', color: 'primary.dark' }
                            }}
                            disabled={icnMediaKitLoading}
                          >
                            {icnMediaKitLoading ? t('landing_media_kit_opening') : t('landing_media_kit_label')}
                          </Button>
                        )}

                        {project.title === 'Band Ahead (2018)' && (
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<LaunchIcon />}
                            onClick={() => window.open(project.youtubeUrl!, '_blank', 'noopener,noreferrer')}
                            sx={{
                              borderColor: 'primary.main',
                              color: 'primary.main',
                              '&:hover': { borderColor: 'primary.dark', color: 'primary.dark' }
                            }}
                          >
                            {t('landing_watch_video')}
                          </Button>
                        )}

                        
                      </Box>
                    </Box>

                    {project.title === 'Feebow HR Tech' && (
                      <Typography variant="body2" color="text.secondary">
                        {t('landing_feebow_info_text')}
                      </Typography>
                    )}

                    {project.title === 'Feebow HR Tech' && (
                      <Box
                        sx={{
                          mt: 2,
                          borderRadius: 2,
                          overflow: 'hidden',
                          width: '100%',
                          maxWidth: 420,
                          mx: 'auto',
                          aspectRatio: '16 / 9',
                          boxShadow: theme.shadows[4],
                          position: 'relative'
                        }}
                      >
                        <Box
                          component="img"
                          src={feebowScreens[feebowCarouselIndex]}
                          alt={`Feebow screenshot ${feebowCarouselIndex + 1}`}
                          sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', cursor: 'zoom-in' }}
                          onClick={() => setFeebowLightboxOpen(true)}
                        />
                        <Box sx={{ position: 'absolute', top: '50%', left: 8, transform: 'translateY(-50%)', zIndex: 1 }}>
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => setFeebowCarouselIndex((feebowCarouselIndex + feebowScreens.length - 1) % feebowScreens.length)}
                            sx={{ minWidth: 0, p: 1 }}
                            aria-label="Previous screenshot"
                          >
                            ‹
                          </Button>
                        </Box>
                        <Box sx={{ position: 'absolute', top: '50%', right: 8, transform: 'translateY(-50%)', zIndex: 1 }}>
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => setFeebowCarouselIndex((feebowCarouselIndex + 1) % feebowScreens.length)}
                            sx={{ minWidth: 0, p: 1 }}
                            aria-label="Next screenshot"
                          >
                            ›
                          </Button>
                        </Box>
                        <Box
                          sx={{
                            position: 'absolute',
                            bottom: 8,
                            left: '50%',
                            transform: 'translateX(-50%)',
                            bgcolor: 'rgba(0,0,0,0.6)',
                            color: 'white',
                            px: 1.5,
                            py: 0.5,
                            borderRadius: 1,
                            fontSize: 12,
                            zIndex: 1
                          }}
                          aria-live="polite"
                        >
                          {`${feebowCarouselIndex + 1}/${feebowScreens.length}`}
                        </Box>
                      </Box>
                    )}

                    {project.title === 'ImigraPulse Canada' && (
                      <Typography variant="body2" color="text.secondary">
                        {t('landing_icn_info_text')}
                      </Typography>
                    )}

                    {project.title === 'ImigraPulse Canada' && (
                      <Box
                        sx={{
                          mt: 2,
                          borderRadius: 2,
                          overflow: 'hidden',
                          width: '100%',
                          maxWidth: 420,
                          mx: 'auto',
                          aspectRatio: '16 / 9',
                          boxShadow: theme.shadows[4],
                          position: 'relative'
                        }}
                      >
                        <Box
                          component="img"
                          src={icnScreens[icnCarouselIndex]}
                          alt={`ImigraPulse screenshot ${icnCarouselIndex + 1}`}
                          sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', cursor: 'zoom-in' }}
                          onClick={() => setIcnLightboxOpen(true)}
                        />
                        <Box sx={{ position: 'absolute', top: '50%', left: 8, transform: 'translateY(-50%)', zIndex: 1 }}>
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => setIcnCarouselIndex((icnCarouselIndex + icnScreens.length - 1) % icnScreens.length)}
                            sx={{ minWidth: 0, p: 1 }}
                            aria-label="Previous screenshot"
                          >
                            ‹
                          </Button>
                        </Box>
                        <Box sx={{ position: 'absolute', top: '50%', right: 8, transform: 'translateY(-50%)', zIndex: 1 }}>
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => setIcnCarouselIndex((icnCarouselIndex + 1) % icnScreens.length)}
                            sx={{ minWidth: 0, p: 1 }}
                            aria-label="Next screenshot"
                          >
                            ›
                          </Button>
                        </Box>
                        <Box
                          sx={{
                            position: 'absolute',
                            bottom: 8,
                            left: '50%',
                            transform: 'translateX(-50%)',
                            bgcolor: 'rgba(0,0,0,0.6)',
                            color: 'white',
                            px: 1.5,
                            py: 0.5,
                            borderRadius: 1,
                            fontSize: 12,
                            zIndex: 1
                          }}
                          aria-live="polite"
                        >
                          {`${icnCarouselIndex + 1}/${icnScreens.length}`}
                        </Box>
                      </Box>
                    )}

                    {project.title === 'ImigraPulse Canada' && (
                      <Dialog open={icnLightboxOpen} onClose={() => setIcnLightboxOpen(false)} maxWidth="lg" fullWidth>
                        <DialogContent sx={{ p: 0, position: 'relative', bgcolor: 'black' }}>
                          <IconButton
                            onClick={() => setIcnLightboxOpen(false)}
                            sx={{ position: 'absolute', top: 8, right: 8, zIndex: 2, color: 'white' }}
                            aria-label="Close"
                          >
                            <CloseIcon />
                          </IconButton>
                          <Box
                            component="img"
                            src={icnScreens[icnCarouselIndex]}
                            alt={`ImigraPulse screenshot expanded ${icnCarouselIndex + 1}`}
                            sx={{ maxWidth: '100%', maxHeight: '80vh', display: 'block', mx: 'auto' }}
                          />
                          <Box sx={{ position: 'absolute', top: '50%', left: 16, transform: 'translateY(-50%)', zIndex: 2 }}>
                            <Button
                              variant="contained"
                              size="small"
                              onClick={() => setIcnCarouselIndex((icnCarouselIndex + icnScreens.length - 1) % icnScreens.length)}
                              sx={{ minWidth: 0, p: 1 }}
                              aria-label="Previous screenshot"
                            >
                              ‹
                            </Button>
                          </Box>
                          <Box sx={{ position: 'absolute', top: '50%', right: 16, transform: 'translateY(-50%)', zIndex: 2 }}>
                            <Button
                              variant="contained"
                              size="small"
                              onClick={() => setIcnCarouselIndex((icnCarouselIndex + 1) % icnScreens.length)}
                              sx={{ minWidth: 0, p: 1 }}
                              aria-label="Next screenshot"
                            >
                              ›
                            </Button>
                          </Box>
                          <Box
                            sx={{
                              position: 'absolute',
                              bottom: 16,
                              left: '50%',
                              transform: 'translateX(-50%)',
                              bgcolor: 'rgba(0,0,0,0.6)',
                              color: 'white',
                              px: 1.5,
                              py: 0.5,
                              borderRadius: 1,
                              fontSize: 12,
                              zIndex: 2
                            }}
                            aria-live="polite"
                          >
                            {`${icnCarouselIndex + 1}/${icnScreens.length}`}
                          </Box>
                        </DialogContent>
                      </Dialog>
                    )}

                    {project.title === 'Feebow HR Tech' && (
                      <Dialog open={feebowLightboxOpen} onClose={() => setFeebowLightboxOpen(false)} maxWidth="lg" fullWidth>
                        <DialogContent sx={{ p: 0, position: 'relative', bgcolor: 'black' }}>
                          <IconButton
                            onClick={() => setFeebowLightboxOpen(false)}
                            sx={{ position: 'absolute', top: 8, right: 8, zIndex: 2, color: 'white' }}
                            aria-label="Close"
                          >
                            <CloseIcon />
                          </IconButton>
                          <Box
                            component="img"
                            src={feebowScreens[feebowCarouselIndex]}
                            alt={`Feebow screenshot expanded ${feebowCarouselIndex + 1}`}
                            sx={{ maxWidth: '100%', maxHeight: '80vh', display: 'block', mx: 'auto' }}
                          />
                          <Box sx={{ position: 'absolute', top: '50%', left: 16, transform: 'translateY(-50%)', zIndex: 2 }}>
                            <Button
                              variant="contained"
                              size="small"
                              onClick={() => setFeebowCarouselIndex((feebowCarouselIndex + feebowScreens.length - 1) % feebowScreens.length)}
                              sx={{ minWidth: 0, p: 1 }}
                              aria-label="Previous screenshot"
                            >
                              ‹
                            </Button>
                          </Box>
                          <Box sx={{ position: 'absolute', top: '50%', right: 16, transform: 'translateY(-50%)', zIndex: 2 }}>
                            <Button
                              variant="contained"
                              size="small"
                              onClick={() => setFeebowCarouselIndex((feebowCarouselIndex + 1) % feebowScreens.length)}
                              sx={{ minWidth: 0, p: 1 }}
                              aria-label="Next screenshot"
                            >
                              ›
                            </Button>
                          </Box>
                          <Box
                            sx={{
                              position: 'absolute',
                              bottom: 16,
                              left: '50%',
                              transform: 'translateX(-50%)',
                              bgcolor: 'rgba(0,0,0,0.6)',
                              color: 'white',
                              px: 1.5,
                              py: 0.5,
                              borderRadius: 1,
                              fontSize: 12,
                              zIndex: 2
                            }}
                            aria-live="polite"
                          >
                            {`${feebowCarouselIndex + 1}/${feebowScreens.length}`}
                          </Box>
                        </DialogContent>
                      </Dialog>
                    )}

                    {project.title === 'Band Ahead (2018)' && (
                      <Box
                        sx={{
                          mt: 2,
                          borderRadius: 2,
                          overflow: 'hidden',
                          width: '100%',
                          maxWidth: 420,
                          mx: 'auto',
                          aspectRatio: '16 / 9',
                          boxShadow: theme.shadows[4]
                        }}
                      >
                        <Box
                          component="iframe"
                          src={`https://www.youtube.com/embed/${(project as any).youtubeId ?? ''}`}
                          title="Band Ahead (2018) - Demo"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                          allowFullScreen
                          loading="lazy"
                          sx={{ width: '100%', height: '100%', border: 0 }}
                        />
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Experience Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography variant="h4" component="h2" textAlign="center" gutterBottom sx={{ mb: 6 }}>
          {t('landing_experience_title')}
        </Typography>
        <Grid container spacing={4}>
          <Grid component={'div' as React.ElementType} item xs={12} md={4}>
            <Paper
              elevation={2}
              sx={{
                p: 4,
                textAlign: 'center',
                height: '100%',
                transition: 'transform 0.3s ease',
                '&:hover': { transform: 'translateY(-4px)' }
              }}
            >
              <CodeIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                {t('landing_experience_years_title')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('landing_experience_years_desc')}
              </Typography>
            </Paper>
          </Grid>
          <Grid component={'div' as React.ElementType} item xs={12} md={4}>
            <Paper
              elevation={2}
              sx={{
                p: 4,
                textAlign: 'center',
                height: '100%',
                transition: 'transform 0.3s ease',
                '&:hover': { transform: 'translateY(-4px)' }
              }}
            >
              <SchoolIcon sx={{ fontSize: 48, color: 'secondary.main', mb: 2 }} />
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                {t('landing_education_title')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('landing_education_desc')}
              </Typography>
            </Paper>
          </Grid>
          
          <Grid component={'div' as React.ElementType} item xs={12} md={4}>
            <Paper
              elevation={2}
              sx={{
                p: 4,
                textAlign: 'center',
                height: '100%',
                transition: 'transform 0.3s ease',
                '&:hover': { transform: 'translateY(-4px)' }
              }}
            >
              <StarIcon sx={{ fontSize: 48, color: 'warning.main', mb: 2 }} />
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                TOP 30 FIAP
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('landing_top30_desc')}
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* Services Section */}
      <Box sx={{ bgcolor: 'primary.main', color: 'white', py: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="h4" component="h2" textAlign="center" gutterBottom sx={{ mb: 6 }}>
            {t('landing_services_title')}
          </Typography>
          <Typography variant="h6" textAlign="center" sx={{ mb: 4, opacity: 0.9 }}>
            {t('landing_services_desc')}
          </Typography>
          <Box sx={{ textAlign: 'center' }}>
            <Button
              variant="contained"
              size="large"
              sx={{
                bgcolor: 'white',
                color: 'primary.main',
                px: 4,
                py: 2,
                fontSize: '1.1rem',
                '&:hover': { bgcolor: 'grey.100' }
              }}
              startIcon={<EmailIcon />}
              onClick={() => handleOpenQuoteModal(ContactEnum.REQUEST_BUDGET)}
            >
              {t('landing_services_cta_quote')}
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Footer */}
      <Box sx={{ bgcolor: 'grey.900', color: 'white', py: 4 }}>
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid component={'div' as React.ElementType} item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>
                WG Tech IT
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.7 }}>
                {t('landing_footer_description')}
              </Typography>
            </Grid>
            <Grid component={'div' as React.ElementType} item xs={12} md={6}>
              <Box sx={{ textAlign: { xs: 'left', md: 'right' } }}>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  {t('landing_footer_contact_label')}
                </Typography>
                <Button
                  color="inherit"
                  startIcon={<EmailIcon />}
                  href="mailto:william@feebow.com"
                  sx={{ mr: 1 }}
                >
                  william@feebow.com
                </Button>
              </Box>
            </Grid>
          </Grid>
          <Divider sx={{ my: 3, bgcolor: 'grey.700' }} />
          <Typography variant="body2" textAlign="center" sx={{ opacity: 0.7 }}>
            {t('landing_page_footer_copyright', { year: new Date().getFullYear() })}
          </Typography>
        </Container>
      </Box>

      {/* Quote Modal */}
      <QuoteModal
        open={quoteModalOpen}
        onClose={handleCloseQuoteModal}
        type={quoteModalType}
        onSubmit={handleQuoteSubmit}
      />
    </Box>
  );
};

export default Landing;
